import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ShieldCheck, ShieldAlert, ListPlus } from "lucide-react";
import { TaskDialog } from "./TasksAdmin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useT } from "@/lib/i18n";
import { api, errMsg, LOCALES } from "@/lib/api";
import { Card, Loader, PageTitle, PositionBadge } from "@/components/kv";

const POSITIONS = ["asci", "asci_yardimcisi", "sanat_hocasi", "barista_head", "barista", "temizlik", "yonetici"];

const StaffDialog = ({ person, onClose, onSaved }) => {
  const { t } = useT();
  const [f, setF] = useState(person
    ? { name: person.name, username: person.username, password: "", position: person.position, weekly_off: person.weekly_off }
    : { name: "", username: "", password: "", position: "barista", weekly_off: [] });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggleDay = (d) => setF((s) => ({ ...s, weekly_off: s.weekly_off.includes(d) ? s.weekly_off.filter((x) => x !== d) : [...s.weekly_off, d] }));
  const save = async () => {
    try {
      if (person) {
        const body = { name: f.name, username: f.username.trim(), position: f.position, weekly_off: f.weekly_off };
        if (f.password) body.password = f.password;
        await api.put(`/admin/staff/${person.id}`, body);
      } else {
        await api.post("/admin/staff", f);
      }
      toast.success(t("common.saved"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, t));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#FAF6F0] rounded-2xl" data-testid="staff-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{person ? t("sadmin.edit") : t("sadmin.add")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>{t("common.name")}</Label><Input value={f.name} onChange={set("name")} className="bg-white" data-testid="staff-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>{t("common.username")}</Label><Input value={f.username} onChange={set("username")} className="bg-white" data-testid="staff-username-input" /></div>
            <div className="space-y-1"><Label>{person ? t("sadmin.new_pw") : t("common.password")}</Label><Input type="password" value={f.password} onChange={set("password")} className="bg-white" data-testid="staff-password-input" /></div>
          </div>
          <div className="space-y-1"><Label>{t("common.position")}</Label>
            <select className="kv-input" value={f.position} onChange={set("position")} data-testid="staff-position-select">
              {POSITIONS.map((p) => <option key={p} value={p}>{t(`positions.${p}`)}</option>)}
            </select>
          </div>
          <div className="space-y-1"><Label>{t("sadmin.weekly_off")}</Label>
            <div className="flex flex-wrap gap-1.5">
              {t("days").map((d, i) => (
                <button key={d} type="button" onClick={() => toggleDay(i)} data-testid={`staff-off-day-${i}`}
                  className={`rounded-full border px-3 py-1 text-sm transition-colors ${f.weekly_off.includes(i) ? "bg-[#46634c] text-white border-[#46634c]" : "bg-white border-[#E8DFD0]"}`}>{d}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button className="rounded-full" onClick={save} data-testid="staff-save-button">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function StaffAdmin() {
  const { t, lang } = useT();
  const [staff, setStaff] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [assignTo, setAssignTo] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch((e) => toast.error(errMsg(e, t)));
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    await api.delete(`/admin/staff/${id}`);
    toast.success(t("common.deleted"));
    load();
  };

  const fmtTs = (iso) => new Intl.DateTimeFormat(LOCALES[lang], { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Istanbul" }).format(new Date(iso));

  return (
    <div>
      <PageTitle kicker={t("adminnav.staff")} title={t("sadmin.title")}>
        <Button className="rounded-full" onClick={() => setDialog({})} data-testid="staff-add-button"><Plus className="h-4 w-4 mr-1" />{t("sadmin.add")}</Button>
      </PageTitle>
      {!staff ? <Loader /> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="staff-list">
          {staff.map((s) => (
            <Card key={s.id} className="p-5 flex flex-col gap-3" data-testid={`staff-card-${s.username}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display text-xl font-bold">{s.name}</div>
                  <div className="font-mono-kv text-xs text-stone-500">@{s.username}</div>
                </div>
                <PositionBadge position={s.position} />
              </div>
              <div className="text-sm text-stone-600">{t("sadmin.weekly_off")}: {s.weekly_off.length ? s.weekly_off.map((d) => t("days")[d]).join(", ") : "—"}</div>
              <div className={`flex items-center gap-1.5 text-xs font-semibold ${s.kvkk_accepted_at ? "text-[#46634c]" : "text-amber-700"}`} data-testid={`staff-consent-${s.username}`}>
                {s.kvkk_accepted_at ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
                {t("sadmin.consent")}: {s.kvkk_accepted_at ? fmtTs(s.kvkk_accepted_at) : t("sadmin.not_consented")}
              </div>
              <div className="flex gap-2 mt-auto pt-2 border-t border-[#E8DFD0]">
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => setDialog({ person: s })} data-testid={`staff-edit-${s.username}`}><Pencil className="h-3.5 w-3.5 mr-1" />{t("common.edit")}</Button>
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => setAssignTo(s)} data-testid={`staff-assign-${s.username}`}><ListPlus className="h-3.5 w-3.5 mr-1" />{t("tadmin.assign")}</Button>
                <Button variant="ghost" size="sm" className="rounded-full text-red-700" onClick={() => remove(s.id)} data-testid={`staff-delete-${s.username}`}><Trash2 className="h-3.5 w-3.5 mr-1" />{t("common.delete")}</Button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {dialog && <StaffDialog person={dialog.person} onClose={() => setDialog(null)} onSaved={load} />}
      {assignTo && <TaskDialog presetUser={assignTo} staff={staff} onClose={() => setAssignTo(null)} onSaved={() => {}} />}
    </div>
  );
}
